import { ACCOUNT_CONSENT_PATHWAY } from '../../../utils/constants';

/**
 * The three parental-consent methods, with their copy.
 *
 * Every string here is verbatim from the SNW, SW Parental Consent Flow deck and
 * its HTML mockup (5 Oct 2026). The method is now set up by the school or network
 * itself, from its own workspace (ParentalConsent.page.jsx) — no longer by the
 * partner at publish.
 *
 * `stages` are the three columns of the Review step — before, when and after the
 * class is published. `needs` lines up with CONSENT_REQUIREMENTS below.
 */

/** The downloadable templates — public GDPR-compliant PDFs in the poweredbydq bucket. */
const PARENTAL_CONSENT_FORM_URL =
    'https://storage.googleapis.com/poweredbydq/resources/Sample%20Parental%20Consent%20Form%20(GDPR-compliant).pdf';
const SCHOOL_AUTHORIZATION_FORM_URL =
    'https://storage.googleapis.com/poweredbydq/resources/Sample%20School%20Authorization%20Form%20(GDPR-compliant).pdf';

const DEADLINE_TEXT =
    'The learner’s account closes and their personal information is permanently deleted. ' +
    'Adding the learner again uses another license.';

export const CONSENT_STAGES = [
    'Before the class is published',
    'When the class is published',
    'After the class is published',
];

export const CONSENT_REQUIREMENTS = ['Learner emails', 'Parent emails', 'Signed consent forms'];

export const CONSENT_PATHWAYS = [
    {
        id: ACCOUNT_CONSENT_PATHWAY.SCHOOL_COLLECTS,
        title: 'Consent by signed form',
        recommended: true,
        summary:
            'School collects written consent in advance, so learners can sign in to their ' +
            'learning space as soon as the class is published.',
        stages: [
            [
                'School collects signed parental consent forms.',
                'School signs the school authorization form (optional).',
                'School adds learners with learner and parent emails.',
            ],
            [
                'Learners receive their account details and can sign in to their learning space.',
                'Parents receive a confirmation and an information letter.',
            ],
            ['School and parents don’t need to do anything further.'],
        ],
        cautionTitle: '',
        deadline: '',
        needs: [true, true, true],
        documents: [
            {
                name: 'Parental consent form',
                desc: 'A template schools can use to collect written approval from parents.',
                optional: false,
                href: PARENTAL_CONSENT_FORM_URL,
            },
            {
                name: 'School authorization form',
                desc:
                    'A short form for schools confirming that parental approval was collected ' +
                    'before their learners’ accounts were created.',
                optional: true,
                href: SCHOOL_AUTHORIZATION_FORM_URL,
            },
        ],
    },
    {
        id: ACCOUNT_CONSENT_PATHWAY.PARENT_CONSENTS_BY_EMAIL,
        title: 'Consent by email',
        recommended: false,
        summary:
            'Learners sign in to their learning space with their own email, and parents give ' +
            'consent separately by email.',
        stages: [
            ['School adds learners with learner and parent emails.'],
            [
                'Learners receive their account details and can sign in to their learning space.',
                'Parents receive a consent-and-activation email and an information letter.',
            ],
            [
                'Parents consent within 7 days.',
                'School follows up with parents who haven’t consented.',
            ],
        ],
        cautionTitle: 'If a parent doesn’t consent within 7 days',
        deadline: DEADLINE_TEXT,
        needs: [true, true, false],
        documents: [],
    },
    {
        id: ACCOUNT_CONSENT_PATHWAY.PARENT_OPENS_SPACE,
        title: 'Consent by sign-in',
        recommended: false,
        summary:
            'Parents give consent by signing in to their child\'s learning space and share the ' +
            'account details with learners.',
        stages: [
            ['School adds learners with parent emails.'],
            [
                'Parents receive a consent-and-activation email with the learner’s account details.',
                'Learners wait for their parent to sign in.',
            ],
            [
                'Parents sign in within 7 days and share the account details with learners.',
                'School follows up with parents who haven’t signed in.',
            ],
        ],
        cautionTitle: 'If a parent doesn’t sign in within 7 days',
        deadline: DEADLINE_TEXT,
        needs: [false, true, false],
        documents: [],
    },
];

export const getConsentPathway = (id) =>
    CONSENT_PATHWAYS.find((p) => p.id === Number(id)) || null;

/**
 * The two questions on "Choose method" map onto a pathway:
 *   school collects                  → 1
 *   Powered by DQ, learners have email → 2
 *   Powered by DQ, no learner email    → 3
 */
export const pathwayFromAnswers = (q1, q2) => {
    if (q1 === 'school') return ACCOUNT_CONSENT_PATHWAY.SCHOOL_COLLECTS;
    if (q1 === 'dq' && q2 === 'yes') return ACCOUNT_CONSENT_PATHWAY.PARENT_CONSENTS_BY_EMAIL;
    if (q1 === 'dq' && q2 === 'no') return ACCOUNT_CONSENT_PATHWAY.PARENT_OPENS_SPACE;
    return null;
};

export const answersFromPathway = (id) => {
    switch (Number(id)) {
        case ACCOUNT_CONSENT_PATHWAY.SCHOOL_COLLECTS: return { q1: 'school', q2: null };
        case ACCOUNT_CONSENT_PATHWAY.PARENT_CONSENTS_BY_EMAIL: return { q1: 'dq', q2: 'yes' };
        case ACCOUNT_CONSENT_PATHWAY.PARENT_OPENS_SPACE: return { q1: 'dq', q2: 'no' };
        default: return { q1: null, q2: null };
    }
};

/** "Consent by signed form" → "consent by signed form", for mid-sentence use. */
export const lowerTitle = (p) => (p ? p.title.toLowerCase() : '');
